import { Directive, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

/**
 * Pointer Events based swipe detector.
 * - Emits swipeleft/swiperight/swipeup/swipedown based on thresholds
 * - Does not call preventDefault; preserves native scroll
 */
@Directive({
  selector: '[swipeGesture]',
  standalone: true,
})
export class SwipeGestureDirective implements OnInit, OnDestroy {
  // Config
  @Input() axis: 'x' | 'y' | 'both' = 'x';
  @Input() thresholdPx = 60; // min movement along main axis
  @Input() maxOffAxisPx = 50; // max movement along the other axis
  @Input() maxDurationMs = 600; // max time for a swipe

  // Outputs
  @Output() swipeleft = new EventEmitter<void>();
  @Output() swiperight = new EventEmitter<void>();
  @Output() swipeup = new EventEmitter<void>();
  @Output() swipedown = new EventEmitter<void>();

  // Ensure we don't block vertical scrolling by default
  @HostBinding('style.touch-action') touchAction: string = 'pan-y';

  private startX = 0;
  private startY = 0;
  private startTime = 0;
  private active = false;

  private onPointerDown = (ev: PointerEvent) => {
    if (!ev.isPrimary) return;
    this.active = true;
    this.startX = ev.clientX;
    this.startY = ev.clientY;
    this.startTime = performance.now();
  };

  private onPointerUpCancel = (ev: PointerEvent) => {
    if (!this.active || !ev.isPrimary) return;
    this.active = false;

    const dt = performance.now() - this.startTime;
    if (dt > this.maxDurationMs) return;

    const dx = ev.clientX - this.startX;
    const dy = ev.clientY - this.startY;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // Determine dominant axis
    const isHorizontal = adx >= ady;

    if ((this.axis === 'x' || (this.axis === 'both' && isHorizontal)) && adx >= this.thresholdPx && ady <= this.maxOffAxisPx) {
      this.zone.run(() => (dx > 0 ? this.swiperight.emit() : this.swipeleft.emit()));
      return;
    }

    if ((this.axis === 'y' || (this.axis === 'both' && !isHorizontal)) && ady >= this.thresholdPx && adx <= this.maxOffAxisPx) {
      this.zone.run(() => (dy > 0 ? this.swipedown.emit() : this.swipeup.emit()));
      return;
    }
  };

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngOnInit(): void {
    const elem = this.el.nativeElement;
    elem.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    elem.addEventListener('pointerup', this.onPointerUpCancel, { passive: true });
    elem.addEventListener('pointercancel', this.onPointerUpCancel, { passive: true });
    elem.addEventListener('pointerleave', this.onPointerUpCancel, { passive: true });
  }

  ngOnDestroy(): void {
    const elem = this.el.nativeElement;
    elem.removeEventListener('pointerdown', this.onPointerDown as any);
    elem.removeEventListener('pointerup', this.onPointerUpCancel as any);
    elem.removeEventListener('pointercancel', this.onPointerUpCancel as any);
    elem.removeEventListener('pointerleave', this.onPointerUpCancel as any);
  }
}
